import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import Footer from '../../components/Footer';
import AppStoreBadges from '../../components/AppStoreBadges';

type Perspective = 'host' | 'rider';

interface PanelContent {
  headline: string;
  body: string;
  highlights: { lead: string; rest: string }[];
  callout: string;
  image: string;
  imageAlt: string;
  mediaFirst: boolean;
}

const GroupPulse: React.FC = () => {
  const { theme, dsColors, colors } = useTheme();
  const [perspective, setPerspective] = useState<Perspective>('host');
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const heroPrimary = colors.textPrimary;
  const heroSecondary = colors.textSecondary;
  const cardBg = colors.surface;
  const cardBorder = colors.border;
  const surfaceSecondary = colors.surfaceSecondary;

  const photoHeading = '#f8fafc';
  const photoBody = 'rgba(226,232,240,0.9)';
  // Directional scrim: stronger where the headline sits, lighter over the riders
  const heroScrim =
    'linear-gradient(105deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.30) 45%, rgba(0,0,0,0.15) 75%, rgba(0,0,0,0.25) 100%)';
  const ctaOverlay = theme === 'light' ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.38)';

  const ringStyle = { '--tw-ring-color': dsColors.primary } as React.CSSProperties;
  const focusClasses = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-0';

  const perspectives: { id: Perspective; label: string }[] = [
    { id: 'host', label: 'Host' },
    { id: 'rider', label: 'Rider' }
  ];

  const selectPerspective = (next: Perspective) => {
    setPerspective(next);
    tabRefs.current[perspectives.findIndex((p) => p.id === next)]?.focus();
  };

  const handleTablistKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const index = perspectives.findIndex((p) => p.id === perspective);
    let nextIndex: number | null = null;
    if (e.key === 'ArrowRight') nextIndex = (index + 1) % perspectives.length;
    if (e.key === 'ArrowLeft') nextIndex = (index - 1 + perspectives.length) % perspectives.length;
    if (e.key === 'Home') nextIndex = 0;
    if (e.key === 'End') nextIndex = perspectives.length - 1;
    if (nextIndex !== null) {
      e.preventDefault();
      selectPerspective(perspectives[nextIndex].id);
    }
  };

  const panelContent: Record<Perspective, PanelContent> = {
    host: {
      headline: 'Your whole crew, one live map.',
      body: 'Turn on GroupPulse for your event and see shared rider locations and participant statuses together on a single live map. Spot a gap before it becomes a lost rider, and make the call that keeps the group together.',
      highlights: [
        {
          lead: 'Know what the map knows',
          rest: '"Signal lost" means the feed dropped; "Location not shared" means they chose not to share.'
        },
        {
          lead: 'Your crew, your structure',
          rest: 'co-hosts, road captains and sweeps are assigned on the event; everyone knows their job before kickstand-up.'
        },
        {
          lead: "You're in charge",
          rest: 'GroupPulse is a Pro feature you switch on per event; riders opt in from there.'
        }
      ],
      callout: "Built for the host who'd rather lead the ride than chase it.",
      image: '/assets/images/ridepulse-hosting-ride-briefing-v1.png',
      imageAlt: 'Riders gathered with their bikes for a pre-ride briefing',
      mediaFirst: true
    },
    rider: {
      headline: 'Join in, on your terms.',
      body: "When your host turns on GroupPulse, you choose how you take part, every ride and every time. Share with the whole crew, with just the host and co-hosts, or not at all. Change your mind mid-ride; you're still in the event, still in the chat, still part of the crew.",
      highlights: [
        {
          lead: 'Three ways to share',
          rest: 'the whole crew, the host and co-hosts, or nothing at all.'
        },
        {
          lead: 'Follow the ride',
          rest: 'when the crew shares with the group, you see them on the live map too.'
        },
        {
          lead: 'Stop anytime',
          rest: 'the host sees "Location not shared," not a frozen dot.'
        }
      ],
      callout: 'You control whether and how you share for the ride.',
      image: '/assets/images/ridepulse-events-cafe-meetup-v1.png',
      imageAlt: 'Riders talking beside their motorcycles outside a cafe',
      mediaFirst: false
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      {/* Hero — desert-road editorial image; type sits in the designed left negative space */}
      <section
        className="relative bg-cover bg-[position:62%_center] md:bg-center"
        style={{ backgroundImage: `${heroScrim}, url('/assets/images/ridepulse-grouppulse-desert-hero-v1.png')` }}
      >
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl py-24 md:py-32 lg:py-40">
          <div className="max-w-md md:max-w-2xl text-center md:text-left mx-auto md:mx-0">
            <p className="text-xs sm:text-sm font-bold uppercase tracking-widest mb-4" style={{ color: photoHeading, opacity: 0.85 }}>
              GroupPulse
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-[1.05]" style={{ color: photoHeading, textWrap: 'balance' }}>
              Every rider. <br />Same page. Same&nbsp;ride.
            </h1>
            <p className="text-lg sm:text-xl leading-relaxed max-w-md mx-auto md:mx-0" style={{ color: photoBody, textWrap: 'pretty' }}>
              Live, opt-in ride tracking that keeps your crew in sync during the ride.
            </p>
          </div>
        </div>
      </section>

      {/* Problem band — quiet, type-led */}
      <section className="py-20 md:py-28" style={{ backgroundColor: cardBg }}>
        <div className="container mx-auto px-4 sm:px-6 max-w-3xl text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold leading-snug mb-6 max-w-2xl mx-auto" style={{ color: heroPrimary, textWrap: 'balance' }}>
            A fuel stop, a red light, a missed turn, and the crew is spread across{' '}
            <span style={{ color: dsColors.primary }}>miles of road</span>.
          </h2>
          <p className="text-lg sm:text-xl leading-relaxed" style={{ color: heroSecondary, textWrap: 'pretty' }}>
            Nobody knows who waited and who kept going. GroupPulse exists so the ride stays one ride.
          </p>
        </div>
      </section>

      {/* Perspective selector + panels — quiet surface; panels swap copy AND media composition */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 sm:px-6 max-w-6xl">
          <div className="text-center mb-10">
            <p className="text-lg sm:text-xl font-semibold mb-6" style={{ color: heroPrimary }}>
              See it from where you sit.
            </p>
            <div
              role="tablist"
              aria-label="GroupPulse perspective"
              className="inline-flex p-1 rounded-full"
              style={{ backgroundColor: surfaceSecondary, border: `1px solid ${cardBorder}` }}
              onKeyDown={handleTablistKeyDown}
            >
              {perspectives.map((p, index) => (
                <button
                  key={p.id}
                  ref={(el) => { tabRefs.current[index] = el; }}
                  role="tab"
                  id={`gp-tab-${p.id}`}
                  aria-selected={perspective === p.id}
                  aria-controls={`gp-panel-${p.id}`}
                  tabIndex={perspective === p.id ? 0 : -1}
                  onClick={() => setPerspective(p.id)}
                  className={`px-6 sm:px-10 py-3 rounded-full font-semibold text-sm sm:text-base min-h-[44px] motion-safe:transition-colors ${focusClasses}`}
                  style={{
                    ...ringStyle,
                    backgroundColor: perspective === p.id ? dsColors.primaryStrong : 'transparent',
                    color: perspective === p.id ? '#ffffff' : heroSecondary
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {perspectives.map((p) => {
            const content = panelContent[p.id];
            const isActive = perspective === p.id;
            const mediaBlock = (
              <div
                role="img"
                aria-label={content.imageAlt}
                className={`rounded-2xl bg-cover border ${content.mediaFirst ? 'aspect-[16/9]' : 'aspect-[4/5] md:aspect-[4/5]'}`}
                style={{
                  backgroundImage: `url('${content.image}')`,
                  backgroundPosition: content.mediaFirst ? 'center 40%' : '62% center',
                  borderColor: cardBorder,
                  boxShadow: '0 12px 32px rgba(0,0,0,0.18)'
                }}
              />
            );
            const copyBlock = (
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold mb-5" style={{ color: heroPrimary, textWrap: 'balance' }}>
                  {content.headline}
                </h2>
                <p className="text-base sm:text-lg leading-relaxed mb-6" style={{ color: heroSecondary, textWrap: 'pretty' }}>
                  {content.body}
                </p>
                <ul className="space-y-4 mb-6">
                  {content.highlights.map((h, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="text-base flex-shrink-0 mt-0.5" style={{ color: dsColors.primary }}>✓</span>
                      <span className="text-sm sm:text-base" style={{ color: heroSecondary }}>
                        <span className="font-bold" style={{ color: heroPrimary }}>{h.lead}</span>
                        {' · '}{h.rest}
                      </span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm sm:text-base font-semibold italic" style={{ color: heroPrimary }}>
                  {content.callout}
                </p>
              </div>
            );
            return (
              <div
                key={p.id}
                role="tabpanel"
                id={`gp-panel-${p.id}`}
                aria-labelledby={`gp-tab-${p.id}`}
                hidden={!isActive}
                tabIndex={isActive ? 0 : undefined}
                className={`${focusClasses} rounded-lg ${isActive ? 'motion-safe:animate-fade-in' : ''}`}
                style={ringStyle}
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-14 items-center">
                  {content.mediaFirst ? (
                    <>
                      <div>{mediaBlock}</div>
                      <div>{copyBlock}</div>
                    </>
                  ) : (
                    <>
                      <div>{copyBlock}</div>
                      <div className="max-w-sm mx-auto md:max-w-none w-full">{mediaBlock}</div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Premium — quiet elevated callout */}
      <section className="py-14 md:py-20" style={{ backgroundColor: cardBg }}>
        <div className="container mx-auto px-4 sm:px-6 max-w-3xl">
          <div
            className="p-8 rounded-2xl text-center"
            style={{
              backgroundColor: surfaceSecondary,
              borderLeft: `4px solid ${dsColors.primary}`,
              boxShadow: '0 12px 32px rgba(0,0,0,0.12)'
            }}
          >
            <h2 className="text-xl sm:text-2xl font-bold mb-3 max-w-xl mx-auto" style={{ color: heroPrimary, textWrap: 'balance' }}>
              One host turns it on. The whole crew benefits.
            </h2>
            <p className="text-base leading-relaxed max-w-xl mx-auto" style={{ color: heroSecondary, textWrap: 'pretty' }}>
              GroupPulse is a Pro feature for the host. Every rider on the event then decides for themselves whether, and how, to share.
            </p>
          </div>
        </div>
      </section>

      {/* Privacy note — compact, quietest point */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4 sm:px-6 max-w-3xl">
          <div
            className="p-8 rounded-2xl"
            style={{ backgroundColor: surfaceSecondary, border: `1px solid ${cardBorder}` }}
          >
            <h2 className="text-lg sm:text-xl font-bold mb-4" style={{ color: heroPrimary }}>
              Your ride, your call.
            </h2>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <span className="text-base flex-shrink-0 mt-0.5" style={{ color: dsColors.primary }}>✓</span>
                <span className="text-sm sm:text-base" style={{ color: heroSecondary }}>Sharing is opt-in per ride, never on by default.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-base flex-shrink-0 mt-0.5" style={{ color: dsColors.primary }}>✓</span>
                <span className="text-sm sm:text-base" style={{ color: heroSecondary }}>It ends when the ride ends.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-base flex-shrink-0 mt-0.5" style={{ color: dsColors.primary }}>✓</span>
                <span className="text-sm sm:text-base" style={{ color: heroSecondary }}>Your profile privacy settings stay exactly as you set them.</span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA + cross-links — real crew photography */}
      <section
        className="relative bg-cover bg-center"
        style={{ backgroundImage: `linear-gradient(${ctaOverlay}, ${ctaOverlay}), url('/assets/images/Pano - SC meetup bike lineup.png')` }}
      >
        <div className="container mx-auto px-4 sm:px-6 max-w-4xl text-center py-24 md:py-32">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-6 leading-tight" style={{ color: photoHeading, textWrap: 'balance' }}>
            Built for the way crews actually ride.
          </h2>
          <p className="text-lg sm:text-xl mb-8 max-w-2xl mx-auto leading-relaxed" style={{ color: photoBody, textWrap: 'pretty' }}>
            Get RidePulse and keep your group together.
          </p>
          <div className="flex justify-center gap-4 flex-wrap mb-10">
            <AppStoreBadges size="large" showApple showAndroid />
          </div>
          <p className="text-base" style={{ color: photoBody }}>
            See how GroupPulse fits the rest of the ride:{' '}
            <Link to="/events" className={`font-semibold underline ${focusClasses}`} style={{ color: photoHeading, ...ringStyle }}>Events</Link>
            {' · '}
            <Link to="/hosting" className={`font-semibold underline ${focusClasses}`} style={{ color: photoHeading, ...ringStyle }}>Hosting</Link>
            {' · '}
            <Link to="/social" className={`font-semibold underline ${focusClasses}`} style={{ color: photoHeading, ...ringStyle }}>Social</Link>
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GroupPulse;
